import React, { useEffect, useState } from 'react';
import { UsersRound } from 'lucide-react';
import { ReactComponent as Network } from 'assets/icon-network.svg';
import { EntityType } from 'util/constants';
import api from 'config/axiosConfig';
import ParticipantsModal from 'components/Actions/ParticipantsModal';
import AlignmentModal from 'components/Alignment/AlignmentModal';
import AlignmentStanceButton from 'components/Alignment/AlignmentStanceButton';
import CreateTeabateArgument from 'components/Teabates/CreateTeabateArgument';
import { getEntityDetails } from 'util/getObjectDetails';
import { toast } from 'sonner';
import PostCreateIconButton from 'ui/PostCreateIconButton';
import { trackEntitySpecificAction } from 'util/analytics';
import { useUser } from 'util/UserContext';
import AuthModal from 'components/Auth/AuthModal';
import { useIssueInfluencesCountQuery } from 'hooks/queries/useIssueQuery';
import { apiRoutes } from "util/apiRoutes";
import { useDrawerNavigation } from "ui/DrawerStack";

interface Props {
  entity: any;
  entityType: EntityType;
  entityId?: number;
  refreshEntity?: () => void;
  actionLabel?: boolean;
  /**
   * Lay out the entity-specific action buttons as Instagram-style vertical
   * stacks (icon on top, count beneath) so they slot into the MediaViewer's
   * right-edge action rail next to Vote/Comment/etc.
   */
  vertical?: boolean;
}

const EntitySpecificActions: React.FC<Props> = ({ entity, entityType, entityId, refreshEntity, actionLabel = true, vertical = false }) => {
  const { openIssueInfluences } = useDrawerNavigation();
  const [participantCount, setParticipantCount] = useState<number>(0);
  const [participantCountLoading, setParticipantCountLoading] = useState<boolean>(false);
  const [showParticipantsModal, setShowParticipantsModal] = useState<boolean>(false);
  const [entityData, setEntityData] = useState<any>(null);
  const { currentUser } = useUser();
  const [showAuthModal, setShowAuthModal] = useState<boolean>(false);
  
  // States for influences
  const [influenceCount, setInfluenceCount] = useState<number>(0);
  const influencesCountQuery = useIssueInfluencesCountQuery(entityType === 'issue' ? entity?.id : null);

  // States for Teabate arguments & sentiment
  const [showAlignmentModal, setShowAlignmentModal] = useState<boolean>(false);
  const [showTeabateArgumentModal, setShowTeabateArgumentModal] = useState<boolean>(false);
  const [teabateArgumentType, setTeabateArgumentType] = useState<'agree' | 'disagree'>('agree');
  const [alignmentEditMode, setAlignmentEditMode] = useState<boolean>(false);
  const [sentiment, setSentiment] = useState({
    agree: 0,
    disagree: 0,
    viewer_sentiment: null as 'agree' | 'disagree' | 'neutral' | null,
  });
  const [sentimentCountLoading, setSentimentCountLoading] = useState<boolean>(false);

  // Teabates arrive on this component as `entityType === 'teabate'` (the
  // canonical post-unification kind). The card layer (`TeabateCard`)
  // routes via the `entity_type = posts.kind` value surfaced by the
  // entity view. The Agree/Disagree stance buttons and the
  // Alignment/Argument modals are gated on this check.
  const isTeabateEntity = entityType === 'teabate';

  useEffect(() => {
    if (entityId) {
      getEntityDetails(entityType, entityId).then((data) => {
        setEntityData(data);
      });
    }
  }, [entityId, entityType]);

  useEffect(() => {
    // Only fetch participants count for actions
    if (entityType === 'action' && entity?.id) {
      // Try to get participant_count from entity first
      if (entity.participant_count !== undefined) {
        setParticipantCount(Number(entity.participant_count));
      } else {
        setParticipantCountLoading(true);
        // Fetch participant count from API if not available
        api.get(apiRoutes.posts.participants.list(entity.id))
          .then(res => setParticipantCount(res.data.total))
          .catch(err => {
            if (err.response && err.response.data && typeof err.response.data.total !== 'undefined') {
              setParticipantCount(err.response.data.total);
            } else {
              console.error('Error getting participant count:', err);
            }
          })
          .finally(() => setParticipantCountLoading(false));
      }
    }

    if (isTeabateEntity && entity?.id) {
        setSentimentCountLoading(true);
        // Sentiment relations on teabate posts use the canonical
        // `node_type = 'teabate'` (matches `posts.kind`) after
        // `backend/sql/migration/2026052602_entity_rel_teabate_node_type.sql`.
        api.get(apiRoutes.entities.relationsCounts, {
            params: {
                node_id: entity.id,
                node_type: 'teabate',
                rel_type: 'sentiment'
            }
        }).then(res => {
            const counts = res.data.aggregates[0] || {};
            setSentiment({
                agree: counts.agree_count || 0,
                disagree: counts.disagree_count || 0,
                viewer_sentiment: res.data.viewer_sentiment,
            });
        }).catch(err => console.error('Error getting sentiment counts:', err))
          .finally(() => setSentimentCountLoading(false));
    }
  }, [entity, entityType, isTeabateEntity]);

  useEffect(() => {
    if (entityType === 'issue') {
      setInfluenceCount(Number(influencesCountQuery.data || 0));
    }
  }, [entityType, influencesCountQuery.data]);


  const handleParticipantsView = () => {
    if (entityType === 'action') {
      // Track participants view action
      trackEntitySpecificAction.viewParticipants(entity.id, participantCount);
      setShowParticipantsModal(true);
    }
  };

  const handleInfluencesView = () => {
    if (entityType === 'issue' && entity?.cname) {
      // Track influences view action
      trackEntitySpecificAction.viewInfluences(entity.id, influenceCount);
      openIssueInfluences(entity.cname);
    }
  };

  // Helper function to check if teabate has dimensions
  const teabateHasDimensions = () => {
    const teabateData = entityId ? entityData : entity;
    return teabateData?.properties?.dimension && 
           teabateData.properties.dimension !== 'none' && 
           teabateData.properties.dimension !== null;
  };

  const handleSentimentClick = async (type: 'agree' | 'disagree') => {
    const currentSentiment = sentiment.viewer_sentiment;
    const hasDimensions = teabateHasDimensions();
    
    // Track sentiment click action
    trackEntitySpecificAction.sentimentClick(entity.id, type, currentSentiment);
    
    // If clicking the same sentiment, immediately remove it
    if (currentSentiment === type) {
      // Optimistically update UI to neutral
      setSentiment(prev => ({
        agree: prev.agree - (type === 'agree' ? 1 : 0),
        disagree: prev.disagree - (type === 'disagree' ? 1 : 0),
        viewer_sentiment: null,
      }));
      
      const sentimentText = type === 'agree' ? 'support' : 'opposition';
      
      // Immediately remove sentiment via API
      try {
        await api.post(apiRoutes.entities.relations, {
          node_id: entity.id,
          node_type: 'teabate',
          rel_type: 'sentiment',
          properties: { value: 'neutral' },
          value_int: 0
        });
        
        // Track sentiment removal
        trackEntitySpecificAction.sentimentRemove(entity.id, type, 'immediate');
        
        // Show toast with undo option (different behavior based on dimensions)
        toast.info(`Your ${sentimentText} to this Teabate was removed`, {
          duration: 5000,
          action: {
            label: hasDimensions ? "Undo and update" : "Undo",
            onClick: async () => {
              // Restore the sentiment in UI
              setSentiment(prev => ({
                agree: prev.agree + (type === 'agree' ? 1 : 0),
                disagree: prev.disagree + (type === 'disagree' ? 1 : 0),
                viewer_sentiment: type,
              }));
              
              // Restore sentiment via API
              try {
                await api.post(apiRoutes.entities.relations, {
                  node_id: entity.id,
                  node_type: 'teabate',
                  rel_type: 'sentiment',
                  properties: { value: type },
                  value_int: type === 'agree' ? 1 : -1
                });
                
                if (hasDimensions) {
                  // Open alignment modal in edit mode for dimension teabates
                  setTeabateArgumentType(type);
                  setAlignmentEditMode(true);
                  setShowAlignmentModal(true);
                }
              } catch (err) {
                console.error('Error restoring sentiment:', err);
                // Revert UI if API call fails
                setSentiment(prev => ({
                  agree: prev.agree - (type === 'agree' ? 1 : 0),
                  disagree: prev.disagree - (type === 'disagree' ? 1 : 0),
                  viewer_sentiment: null,
                }));
              }
            }
          },
        });
      } catch (err) {
        console.error('Error removing sentiment:', err);
        // Restore the UI if API call fails
        setSentiment(prev => ({
          agree: prev.agree + (type === 'agree' ? 1 : 0),
          disagree: prev.disagree + (type === 'disagree' ? 1 : 0),
          viewer_sentiment: type,
        }));
      }
      
      return;
    }
    
    // Otherwise, handle as normal sentiment change
    const newSentimentType = type;
    const previousSentiment = { ...sentiment };

    // Optimistically update UI first to make it feel instant
    setSentiment(prev => {
        const newCounts = { agree: prev.agree, disagree: prev.disagree };

        // Decrement previous sentiment if one was selected
        if (prev.viewer_sentiment === 'agree') {
            newCounts.agree--;
        } else if (prev.viewer_sentiment === 'disagree') {
            newCounts.disagree--;
        }

        // Increment new sentiment
        if (newSentimentType === 'agree') {
            newCounts.agree++;
        } else if (newSentimentType === 'disagree') {
            newCounts.disagree++;
        }

        return {
            ...newCounts,
            viewer_sentiment: newSentimentType as any,
        };
    });

    // Perform the API call in the background
    try {
        await api.post(apiRoutes.entities.relations, {
            node_id: entity.id,
            node_type: 'teabate',
            rel_type: 'sentiment',
            properties: { value: newSentimentType },
            value_int: newSentimentType === 'agree' ? 1 : newSentimentType === 'disagree' ? -1 : 0
        });
    } catch (error) {
        console.error('Error posting sentiment:', error);
        // If the API call fails, revert the UI to the previous state
        setSentiment(previousSentiment);
        return; // Don't proceed with modal/toast logic if API failed
    }

    if (hasDimensions) {
      // For teabates with dimensions, check for existing alignment and open alignment modal
      let hasExistingAlignment = false;
      try {
        const { data } = await api.get(apiRoutes.user.alignmentByTeabate(entity.id));
        hasExistingAlignment = Boolean(data && (data.id || data.teabate_id));
      } catch (err) {
        // If the check fails, default to non-edit mode (share flow)
        hasExistingAlignment = false;
      }

      // Track alignment modal opening
      trackEntitySpecificAction.alignmentModalOpen(entity.id, newSentimentType, hasExistingAlignment);
      
      // Open the alignment modal with appropriate mode
      setTeabateArgumentType(newSentimentType);
      setAlignmentEditMode(hasExistingAlignment);
      setShowAlignmentModal(true);
    } else {
      // For teabates without dimensions, show toast with argument option
      const sentimentText = newSentimentType === 'agree' ? 'Support' : 'Opposition';
      
      toast.success(`${sentimentText} added for teabate`, {
        duration: 3200,
        action: {
          label: "Add argument",
          onClick: () => handleOpenArgumentFromToast(entityId ? entityData : entity, newSentimentType),
        },
      });
    }
  };

  const handleOpenArgumentFromToast = (teabate: any, stance: 'agree' | 'disagree') => {
    // Track argument modal opening from toast
    trackEntitySpecificAction.argumentModalOpen(entity.id, stance);
    
    setTeabateArgumentType(stance);
    setShowTeabateArgumentModal(true);
  };

  const handleArgumentSubmitSuccess = () => {
    if(refreshEntity) {
      refreshEntity();
    }
  };

  // Render different actions based on entity type
  const renderEntityActions = () => {
    if (entityType === 'action') {
      if (vertical) {
        return (
          <button
            type="button"
            className="action participant flex flex-col items-center gap-0.5 text-eoe-gray dark:text-eoe-dark-icon-stroke hover:bg-white/15 rounded-full p-1 transition-colors"
            onClick={handleParticipantsView}
            title="View participants"
            aria-label="View participants"
          >
            <UsersRound className="size-7" />
            <span className="text-xs font-semibold leading-none">
              {participantCountLoading ? "…" : participantCount}
            </span>
          </button>
        );
      }
      return (
        <button
          className="h-7 rounded-full px-1.5 text-eoe-gray dark:text-eoe-dark-icon-stroke dark:hover:text-eoe-dark-icon-stroke-hover flex justify-center items-center hover:bg-gray-300 dark:hover:bg-eoe-dark-icon-hover transition-colors"
          onClick={handleParticipantsView}
          title="View participants"
        >
          <div className="flex items-center gap-0.5">
            <UsersRound className="size-5" />
            {participantCountLoading ? (
              <span className="text-xs font-semibold">...</span>
            ) : participantCount > 0 ? (
              <span className="text-sm font-semibold">{participantCount}</span>
            ) : null}
          </div>
        </button>
      );
    } else if (entityType === 'issue') {
      if (vertical) {
        return (
          <div className="flex flex-col items-center gap-3">
            {entity.properties.type !== 'Compound' && (
              <button
                type="button"
                className="action influence flex flex-col items-center gap-0.5 text-eoe-gray dark:text-eoe-dark-icon-stroke hover:bg-white/15 rounded-full p-1 transition-colors"
                onClick={handleInfluencesView}
                title="View influences"
                aria-label="View influences"
              >
                <Network className="size-7" />
                <span className="text-xs font-semibold leading-none">
                  {influencesCountQuery.isLoading ? "…" : influenceCount}
                </span>
              </button>
            )}
            <div
              onClickCapture={(e) => {
                trackEntitySpecificAction.postCreate(entity.id);
                if (!currentUser) {
                  e.preventDefault();
                  e.stopPropagation();
                  setShowAuthModal(true);
                }
              }}
            >
              <PostCreateIconButton
                initialIssue={entity}
                title="Create post for this issue"
                className="p-1 size-9 rounded-full hover:bg-white/15"
              />
            </div>
          </div>
        );
      }
      return (
        <div className="flex items-center gap-x-1">
          {entity.properties.type !== 'Compound' && (
          <button
            className="h-7 rounded-full px-1 text-eoe-gray dark:text-eoe-dark-icon-stroke dark:hover:text-eoe-dark-icon-stroke-hover flex justify-center items-center hover:bg-gray-300 dark:hover:bg-eoe-dark-icon-hover transition-colors"
            onClick={handleInfluencesView}
            title="View influences"
          >
            <div className="flex items-center gap-0.5">
              <Network className="size-5" />
              <span className="text-sm font-semibold">{influencesCountQuery.isLoading ? "..." : influenceCount}</span>
            </div>
          </button>
          )}
          <div
            onClickCapture={(e) => {
              // Track the intent to create a post
              trackEntitySpecificAction.postCreate(entity.id);
              // Gate by authentication: prevent child click from opening if not logged in
              if (!currentUser) {
                e.preventDefault();
                e.stopPropagation();
                setShowAuthModal(true);
              }
            }}
          >
            <PostCreateIconButton
              initialIssue={entity}
              title="Create post for this issue"
              buttonText="Spill"
              className="h-7 px-1.5 p-0"
            />
          </div>
        </div>
      );
    } else if (isTeabateEntity) {
      // The agree/disagree alignment buttons keep their horizontal pairing
      // even in the vertical media-rail; they are conceptually a single
      // stance widget. The wrapping container just stacks them vertically
      // for the rail when vertical mode is on.
      return (
        <div className={vertical ? "flex flex-col items-center gap-1" : "flex items-center gap-x-0.5"}>
            <AlignmentStanceButton
              variant="disagree"
              isSelected={sentiment.viewer_sentiment === 'disagree'}
              count={sentimentCountLoading ? 0 : sentiment.disagree}
              showCount={true}
              showText={vertical ? false : actionLabel}
              onClick={() => handleSentimentClick('disagree')}
            />
            <AlignmentStanceButton
              variant="agree"
              isSelected={sentiment.viewer_sentiment === 'agree'}
              count={sentimentCountLoading ? 0 : sentiment.agree}
              showCount={true}
              showText={vertical ? false : actionLabel}
              onClick={() => handleSentimentClick('agree')}
            />
        </div>
      );
    }
    return null;
  };

  return (
    <>
      <div className={`entity-specific-actions flex ${vertical ? "flex-col items-center" : "items-center"}`}>
        {renderEntityActions()}
      </div>

      {/* Participants Modal */}
      {showParticipantsModal && entityType === 'action' && (
        <ParticipantsModal
          actionId={entity.id}
          onClose={() => setShowParticipantsModal(false)}
        />
      )}

      {/* Alignment Modal - only for teabates with dimensions */}
      {showAlignmentModal && isTeabateEntity && teabateHasDimensions() && (
        <AlignmentModal
          isOpen={showAlignmentModal}
          onClose={() => {
            setShowAlignmentModal(false);
            setAlignmentEditMode(false);
          }}
          teabate={entityId ? entityData : entity}
          stance={teabateArgumentType}
          editMode={alignmentEditMode}
          onOpenArgumentModal={handleOpenArgumentFromToast}
        />
      )}

      {/* Teabate Argument Modal */}
      {showTeabateArgumentModal && isTeabateEntity && (
        <CreateTeabateArgument
            isOpen={showTeabateArgumentModal}
            onClose={() => setShowTeabateArgumentModal(false)}
            teabate={entityId ? entityData : entity}
            argumentType={teabateArgumentType}
            onSubmitSuccess={handleArgumentSubmitSuccess}
            onReopenModal={() => setShowTeabateArgumentModal(true)}
        />
      )}

      {showAuthModal && (
        <AuthModal
          show={showAuthModal}
          onClose={() => setShowAuthModal(false)}
        />
      )}
    </>
  );
};

export default EntitySpecificActions; 